import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getClientId,
  getDistributionSummaryCardsRest,
  type DistributionSummaryCardItem,
} from "../services/distributionService";
import { resolveWebSocketGatewayOrigin } from "../runtime/loadSharedDashboardConfig";

function buildDistributionSummaryCardsWsUrl(params: {
  clientId: string;
  type: string;
  from?: string;
  to?: string;
  locationId: string;
  cameraId: string;
}): string {
  const wsOrigin = resolveWebSocketGatewayOrigin();
  const u = new URL(`${wsOrigin}/analytic/distribution-summary-cards`);
  u.searchParams.set("clientId", params.clientId);
  u.searchParams.set("type", params.type);
  if (params.from) u.searchParams.set("from", params.from);
  if (params.to) u.searchParams.set("to", params.to);
  u.searchParams.set("locationId", params.locationId);
  u.searchParams.set("cameraId", params.cameraId);
  return u.toString();
}

type WsMessage =
  | { type: "connected"; client_id?: string; message?: string }
  | {
      type: "summary_cards";
      stream_type?: string;
      data: DistributionSummaryCardItem[];
      timestamp?: string;
    };

/** Avoid calling close() while CONNECTING — React Strict Mode unmounts quickly and that triggers
 * "WebSocket is closed before the connection is established" in the console. */
function disposeWebSocket(socket: WebSocket | null) {
  if (!socket) return;
  socket.onopen = null;
  socket.onmessage = null;
  socket.onerror = null;
  socket.onclose = null;
  const { readyState } = socket;
  if (readyState === WebSocket.CONNECTING) {
    socket.addEventListener(
      "open",
      () => {
        try {
          socket.close();
        } catch {
          /* ignore */
        }
      },
      { once: true }
    );
  } else if (readyState === WebSocket.OPEN) {
    try {
      socket.close();
    } catch {
      /* ignore */
    }
  }
}

export function useDistributionSummaryCards(params: {
  from?: string;
  to?: string;
  location_id?: string;
  camera_id?: string;
  type: string;
  enabled?: boolean;
}) {
  const { enabled = true, type, from, to, location_id, camera_id } = params;
  const clientId = getClientId();
  const queryEnabled = enabled && !!clientId;

  const restQuery = useQuery({
    queryKey: ["distribution-summary-cards", "rest", type, from, to, location_id, camera_id],
    queryFn: () =>
      getDistributionSummaryCardsRest({
        type,
        from,
        to,
        location_id,
        camera_id,
      }),
    enabled: queryEnabled,
    staleTime: 30 * 1000,
    retry: 1,
    retryDelay: 2000,
  });

  const [wsCards, setWsCards] = useState<DistributionSummaryCardItem[] | null>(null);
  const [wsConnected, setWsConnected] = useState(false);

  const loc = typeof location_id === "string" ? location_id : "";
  const cam = typeof camera_id === "string" ? camera_id : "";

  const wsKey = useMemo(
    () => `${type}|${from ?? ""}|${to ?? ""}|${loc}|${cam}`,
    [type, from, to, loc, cam]
  );

  useEffect(() => {
    if (!queryEnabled || !clientId) {
      setWsCards(null);
      setWsConnected(false);
      return;
    }

    setWsCards(null);
    setWsConnected(false);

    const url = buildDistributionSummaryCardsWsUrl({
      clientId,
      type,
      from,
      to,
      locationId: loc,
      cameraId: cam,
    });

    let cancelled = false;
    let ws: WebSocket | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      if (cancelled) return;
      try {
        ws = new WebSocket(url);
      } catch {
        return;
      }

      ws.onopen = () => {
        if (cancelled) return;
      };

      ws.onmessage = (event) => {
        if (cancelled) return;
        try {
          const msg = JSON.parse(event.data as string) as WsMessage;
          if (msg.type === "connected") {
            setWsConnected(true);
            return;
          }
          if (msg.type === "summary_cards" && Array.isArray(msg.data)) {
            if (msg.stream_type && msg.stream_type !== type) return;
            setWsCards(msg.data);
          }
        } catch {
          // ignore malformed frames
        }
      };

      ws.onerror = () => {
        // keep REST data; optional: could set a flag
      };

      ws.onclose = () => {
        if (cancelled) return;
        setWsConnected(false);
        retryTimer = setTimeout(() => {
          if (!cancelled) connect();
        }, 3000);
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (retryTimer != null) clearTimeout(retryTimer);
      disposeWebSocket(ws);
      ws = null;
    };
  }, [queryEnabled, clientId, wsKey, type, from, to, loc, cam]);

  const restData = restQuery.data ?? [];
  const data = wsCards != null ? wsCards : restData;
  const isLoading = restQuery.isLoading && wsCards == null;
  const isError = restQuery.isError && wsCards == null;

  return {
    data,
    isLoading,
    isError,
    isFetching: restQuery.isFetching,
    wsConnected,
    usingWebSocketData: wsCards != null,
    refetch: restQuery.refetch,
  };
}
